
import * as React from 'react';
import { IOnExecuteProps } from '../commandSet/CommandSet';
import { isCancel } from '../../utilities/async/CancelError';
import { IEngagementData, ResultType } from '../../facets/LoggerFacets';
import { ITelemetryConsumerStateProps, withTelemetry } from '../telemetry/TelemetryProvider';
import { Engagement, IEngagementSingleSchema } from '@ms/telemetry-manager/lib/events/Engagement.event';
import { Qos, IQosStartSchema } from '@ms/telemetry-manager/lib/events/Qos.event';
import { IActionBaseProps, ActionBase } from '../withAction/WithAction';

export { IActionBaseProps };
export { ActionBase };

/**
 * @public
 */
export type IActionProps = IActionBaseProps & {
  /**
   * Data used to fire Engagement events when the Action is invoked.
   * This data is collected while the action initially evaluates, which provides
   * more guarantees about accuracy but is potentially slower due to its impact on rendering.
   */
  engagement?: IEngagementData;
  /**
   * Callback to get more data for an Engagement event once the action is invoked.
   */
  getEngagement?: () => Promise<Partial<IEngagementData>>
};

// Add prop for engagement logging
const mapProps = (
  telemetryProps: ITelemetryConsumerStateProps,
  props: Pick<IActionBaseProps, 'onExecute'> & Pick<IActionProps, 'engagement' | 'getEngagement'>
): Pick<IActionBaseProps, 'onExecute'> => {
  const {
    telemetry
  } = telemetryProps;

  const {
    onExecute,
    engagement = {
      name: 'Action'
    },
    getEngagement
  } = props;

  if (!onExecute) {
    return {};
  }

  return {
    onExecute: async (onExecuteProps: IOnExecuteProps) => {
      const finalEngagement: IEngagementData = {
        ...(getEngagement ? await getEngagement() : {}),
        ...engagement
      };

      const {
        name
      } = finalEngagement;

      telemetry(Engagement).withData((data: IEngagementSingleSchema) => {
        return {
          ...data,
          ...finalEngagement
        };
      }).logData({
        name: name
      });

      const qos = new (telemetry(Qos).withData((data: IQosStartSchema) => {
        return {
          ...data
        };
      }))({ name });

      try {
        await onExecute(onExecuteProps);

        qos.end({
          resultType: ResultType.success
        });
      } catch (e) {
        if (isCancel(e)) {
          qos.end({
            resultType: ResultType.expectedFailure
          });
        } else {
          qos.end({
            resultType: ResultType.failure
          });
        }

        throw e;
      }
    }
  };
};

/**
 * Renders an `Action` which, when evaluated with a `WithAction` or `WithActionSet` component,
 * tracks the state of the action and updates the host component with the information
 * regarding availability, toggled state, and execution behavior.
 *
 * Create components which render `<Action { ... } />` which can themselves be uses as actions.
 * Wrappers may accept custom props, connect to the item cache, or subscribe to external
 * events or contexts, and may be stateful.
 *
 * @public
 *
 * ```tsx
 *  class Foo extends React.PureComponent<IFooProps> {
 *    public render(): JSX.Element {
 *      const {
 *        itemKey
 *      } = this.props;
 *
 *      return (
 *        <WithItemCache>
 *          { ({ itemCache, dispatch }: IItemCacheStateProps) => {
 *            const foo = itemCache.demandItemFacet(fooFacet, itemKey);
 *            return (
 *              <Action
 *                isAvailable={ !!foo && foo > 2 }
 *                onExecute={ async () => {
 *                  await dispatch(doStuff({ itemKey }));
 *                } }
 *              />
 *            );
 *          } }
 *        </WithItemCache>
 *      );
 *    }
 *  }
 * ```
 */
export const Action: React.ComponentType<IActionProps> = withTelemetry(mapProps)(ActionBase);

Action.displayName = 'Action';
